import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { OrdersStartRealizationListDataSource } from './orders-start-realization-list.datasource';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { OrdersStartRealizationListFilterService } from '../services/order-start-realization-list-filter-service';
import { Filter } from 'src/app/models/common/filter';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { EMPTY, finalize, first, firstValueFrom, merge, Subscription, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ScreenService } from 'src/app/core/screen.service';
import { OrderService } from 'src/app/data/order.service';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Messages, Permission } from 'src/app/common/enums';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { OrderStartRealizationItemsDto } from 'src/app/models/dtos/orders/order-start-realization-items-dto';
import { FormControl, Validators } from '@angular/forms';
import { EditWorkerRealizationDateRequest } from 'src/app/contracts/requests/orders/edit-worker-realization-date-request';
import { trigger, transition, animate } from '@angular/animations';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MassWorkerRealizationDateEditModalComponent } from './mass-worker-realization-date-edit-modal/mass-worker-realization-date-edit-modal.component';

const DEFAULT_SORT_FIELD = 'LastName';

@Component({
  selector: 'app-orders-start-realization-list',
  templateUrl: './orders-start-realization-list.component.html',
  styleUrl: './orders-start-realization-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrdersStartRealizationListDataSource, { provide: FilterService, useClass: OrdersStartRealizationListFilterService }],
  standalone: false
})
export class OrdersStartRealizationListComponent implements AfterViewInit, OnInit, OnDestroy {
  screenService = inject(ScreenService);
  dataSource = inject(OrdersStartRealizationListDataSource);
  private translateService = inject(TranslateService);
  private spinner = inject(NgxSpinnerService);
  private filterService = inject(FilterService);
  private orderService = inject(OrderService);
  private dialog = inject(MatDialog);
  private snackbar = inject(SnackBarService);
  private authService = inject(AuthService);

  displayedColumns = [
    'select',
    'workerFullName',
    'employerName',
    'employerObjectName',
    'positionExpectationName',
    'submissionDate',
    'startDate',
    'workerRealizationDate',
    'actions',
  ];

  filters: Filter[] = [];
  dateControl: FormControl | null = null;
  maxDate: Date = new Date();
  editingElement: OrderStartRealizationItemsDto | null = null;

  private _areFiltersExpanded: boolean;
  public get areFiltersExpanded(): boolean {
    return this._areFiltersExpanded;
  }
  public set areFiltersExpanded(value: boolean) {
    if (this._areFiltersExpanded !== value) {
      this._areFiltersExpanded = value;
    }
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private subscriptions: Subscription[] = [];

  constructor() { }

  ngOnInit(): void {
    this.initDateControl();

    this.filterService.filters$.subscribe((filters) => {
      this.filterData(filters);
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngAfterViewInit(): void {
    this.sort.sort({ id: DEFAULT_SORT_FIELD, start: 'asc' } as MatSortable);

    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0)));

    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange).subscribe(() => this.fetchOrderStartRealizationItems()),
    );

    this.subscriptions.push(
      this.dataSource.loading$.subscribe({
        next: (isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()),
        complete: () => this.spinner.hide(),
      }),
    );

    this.fetchOrderStartRealizationItems();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
  }

  initDateControl() {
    this.dateControl = new FormControl(null, Validators.required);
  }

  startEdit(element: OrderStartRealizationItemsDto): void {
    this.editingElement = element;
    const initialDate = element.WorkerRealizationDate ? new Date(element.WorkerRealizationDate) : null;
    this.dateControl.setValue(initialDate);
  }

  finishEditing(): void {
    this.editingElement = null;
    this.dateControl.reset();
  }

  isPossibleToEdit(element: OrderStartRealizationItemsDto): boolean {
    return element.StartDate.getTime() <= new Date().getTime();
  }

  saveDate(element: OrderStartRealizationItemsDto) {
    if (this.dateControl && this.dateControl.valid) {
      let req: EditWorkerRealizationDateRequest = {
        workerRealizationDate: this.dateControl.value,
        workerOrderItemIds: [element.WorkerOrderItemId],
      };
      this.spinner.show();

      this.orderService.editWorkerRealizationDate(req).subscribe({
        next: () => {
          this.finishEditing();
          this.snackbar.openSuccessSnackBar(Messages.SuccessfullyEditWorkerRealizationDate);
          this.fetchOrderStartRealizationItems();
        },
        complete: () => this.spinner.hide(),
        error: (error: HttpErrorResponse) => {
          this.snackbar.openErrorSnackBar(error.error);
          this.spinner.hide();
        },
      });
    }
  }

  hasAnyAction(order: OrderStartRealizationItemsDto): boolean {
    return this.clearWorkerRealizationDateButtonVisible(order);
  }

  clearWorkerRealizationDateButtonVisible = (order: OrderStartRealizationItemsDto): boolean =>
    order.WorkerRealizationDate && this.isPossibleToEdit(order) && this.hasPermissionToChangeWorkerRealizationDate();

  hasPermissionToChangeWorkerRealizationDate(): boolean {
    return this.authService.hasPermission(Permission.ChangeOrderItemWorkerRealizationDate);
  }

  isAllSelected = () => {
    return this.dataSource.selection.selected.length === this.dataSource.orderStartRealizationItemsSubject.value.length;
  };

  masterToggle = () =>
    this.isAllSelected()
      ? this.dataSource.selection.clear()
      : this.dataSource.orderStartRealizationItemsSubject.value.forEach((row) => this.dataSource.selection.select(row));

  isMassEditWorkerRealizationDateButtonEnabled(): boolean {
    if (this.dataSource.selection.selected.length === 0) {
      return false;
    }

    const firstElement = this.dataSource.selection.selected[0];
    const referenceStartDate = firstElement.StartDate.getTime();
    const referenceExpectationId = firstElement.PositionExpectationId;

    return this.dataSource.selection.selected.every(
      (element) =>
        this.isPossibleToEdit(element) &&
        element.StartDate.getTime() === referenceStartDate &&
        element.PositionExpectationId === referenceExpectationId,
    );
  }

  openMassEditWorkerRealizationDate() {
    if (this.dataSource.selection.selected && this.dataSource.selection.selected.length === 0) {
      this.snackbar.openErrorSnackBar(Messages.InvalidWorkerOrderItemsToEdit);
      return;
    }

    const dialogRef = this.dialog.open(MassWorkerRealizationDateEditModalComponent, { data: this.dataSource.selection.selected });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchOrderStartRealizationItems();
      }
    });
  }

  async cleanWorkerRealizationDate(id: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.orderService.cleanWorkerRealizationDate(id).pipe(
        first(),
        tap((_) => this.fetchOrderStartRealizationItems()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCleanWorkerRealizationDate)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.CleanWorkerRealizationDateTitle,
            message: Messages.CleanWorkerRealizationDateMessage,
            confirmButtonLabel: this.translateService.instant(Messages.CleanWorkerRealizationDateConfirmButton),
            dismissButtonLabel: this.translateService.instant(Messages.OrderDeclineButton),
          },
        })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  async resetFilters(): Promise<void> {
    await firstValueFrom(this.filterService.resetFilters());
  }

  async filterData(filters: Filter[]): Promise<void> {
    this.filters = filters;
    this.fetchOrderStartRealizationItems();
  }

  private fetchOrderStartRealizationItems(resetPage: boolean = false) {
    if (resetPage) {
      this.paginator.pageIndex = 0;
    }

    this.dataSource.selection.clear();
    this.dataSource.fetchOrderStartRealizationItems(
      this.paginator.pageIndex + 1,
      this.paginator.pageSize,
      this.sort.active,
      this.sort.direction,
      this.filters,
    );
  }
}
